'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { companySettingsService } from '@/services/companySettings.service';
import { hasModuleAccess, hasAnyApprovePermission } from '@/lib/permissions';
import { usePendingApprovals } from '@/hooks/usePendingApprovals';
import {
  LayoutDashboard,
  FileText,
  ShoppingCart,
  Users,
  ChevronLeft,
  ChevronRight,
  Settings,
  User,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Truck,
  FileCheck,
  CreditCard,
  Wallet,
  Banknote,
  BarChart2,
  Package,
  Warehouse,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  FolderKanban,
  CheckSquare,
  Calendar,
  UserCog,
  TrendingUp,
  PieChart,
  ClipboardList,
  Globe,
  GitBranch,
  Shield,
  Hash,
  Sliders,
  Receipt,
  DollarSign,
  BookOpen,
  Layers,
  Database,
  Calculator,
  X,
  ClipboardCheck,
  Link2,
  Percent,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  badge?: number;
  // Only set on items whose group spans multiple modules (Reports) — filtered individually.
  // Items without this inherit visibility from their group's `module`.
  module?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
  defaultOpen?: boolean;
  // Permission module this group maps to. Undefined (Dashboard) means always visible.
  module?: string;
}

const navGroups: NavGroup[] = [
  {
    id: 'main',
    label: 'Dashboard',
    icon: <LayoutDashboard size={16} />,
    defaultOpen: true,
    items: [
      { id: 'nav-dashboard', label: 'Dashboard', icon: <LayoutDashboard size={15} />, href: '/' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: <ShoppingCart size={16} />,
    module: 'Sales',
    defaultOpen: true,
    items: [
      {
        id: 'nav-penawaran',
        label: 'Penawaran',
        icon: <FileText size={15} />,
        href: '/riwayat-penawaran',
      },
      {
        id: 'nav-customer-po',
        label: 'Customer PO',
        icon: <FileCheck size={15} />,
        href: '/customer-po',
      },
      {
        id: 'nav-sales-order',
        label: 'Sales Order',
        icon: <ShoppingCart size={15} />,
        href: '/sales-order',
      },
      { id: 'nav-invoice', label: 'Invoice', icon: <Receipt size={15} />, href: '/invoice' },
      { id: 'nav-customer', label: 'Customer', icon: <Users size={15} />, href: '/customer' },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    icon: <ShoppingBag size={16} />,
    module: 'Purchasing',
    defaultOpen: false,
    items: [
      {
        id: 'nav-purchase-request',
        label: 'Purchase Request',
        icon: <ClipboardList size={15} />,
        href: '/purchase-request',
      },
      {
        id: 'nav-purchase-order',
        label: 'Purchase Order',
        icon: <ShoppingBag size={15} />,
        href: '/purchase-order',
      },
      {
        id: 'nav-supplier-invoice',
        label: 'Bill',
        icon: <FileCheck size={15} />,
        href: '/supplier-invoice',
      },
      { id: 'nav-vendor', label: 'Vendor', icon: <Truck size={15} />, href: '/vendor' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: <Wallet size={16} />,
    module: 'Finance',
    defaultOpen: false,
    items: [
      {
        id: 'nav-ar',
        label: 'Accounts Receivable',
        icon: <TrendingUp size={15} />,
        href: '/accounts-receivable',
      },
      {
        id: 'nav-ap',
        label: 'Accounts Payable',
        icon: <CreditCard size={15} />,
        href: '/accounts-payable',
      },
      {
        id: 'nav-cash-in',
        label: 'Cash In',
        icon: <ArrowDownCircle size={15} />,
        href: '/cash-in',
      },
      {
        id: 'nav-cash-out',
        label: 'Cash Out',
        icon: <ArrowUpCircle size={15} />,
        href: '/cash-out',
      },
      {
        id: 'nav-expense',
        label: 'Expense Management',
        icon: <Receipt size={15} />,
        href: '/expense',
      },
      {
        id: 'nav-expense-category',
        label: 'Kategori Pengeluaran',
        icon: <Layers size={15} />,
        href: '/expense-category',
      },
      { id: 'nav-bank', label: 'Bank', icon: <Banknote size={15} />, href: '/bank' },
      {
        id: 'nav-bank-reconciliation',
        label: 'Rekonsiliasi Bank',
        icon: <RefreshCw size={15} />,
        href: '/bank-reconciliation',
      },
      {
        id: 'nav-finance-reports',
        label: 'Finance Reports',
        icon: <BookOpen size={15} />,
        href: '/finance-reports',
      },
    ],
  },
  {
    id: 'accounting',
    label: 'Accounting',
    icon: <Calculator size={16} />,
    module: 'Accounting',
    defaultOpen: false,
    items: [
      {
        id: 'nav-journal-entry',
        label: 'Journal Entry',
        icon: <BookOpen size={15} />,
        href: '/journal-entry',
      },
      {
        id: 'nav-opening-balance',
        label: 'Opening Balance',
        icon: <Calculator size={15} />,
        href: '/opening-balance',
      },
      {
        id: 'nav-trial-balance',
        label: 'Trial Balance',
        icon: <Layers size={15} />,
        href: '/finance-reports/trial-balance',
      },
      {
        id: 'nav-laba-rugi',
        label: 'Laba Rugi',
        icon: <TrendingUp size={15} />,
        href: '/finance-reports/laba-rugi',
      },
      {
        id: 'nav-neraca',
        label: 'Neraca',
        icon: <Database size={15} />,
        href: '/finance-reports/neraca',
      },
      {
        id: 'nav-ppn-reconciliation',
        label: 'Rekapitulasi PPN',
        icon: <Receipt size={15} />,
        href: '/finance-reports/ppn-reconciliation',
      },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: <Package size={16} />,
    module: 'Inventory',
    defaultOpen: false,
    items: [
      {
        id: 'nav-item-master',
        label: 'Item Master',
        icon: <Layers size={15} />,
        href: '/item-master',
      },
      {
        id: 'nav-warehouse',
        label: 'Warehouse',
        icon: <Warehouse size={15} />,
        href: '/warehouse',
      },
      {
        id: 'nav-stock-in',
        label: 'Stock In',
        icon: <ArrowDownCircle size={15} />,
        href: '/stock-in',
      },
      {
        id: 'nav-stock-out',
        label: 'Stock Out',
        icon: <ArrowUpCircle size={15} />,
        href: '/stock-out',
      },
      {
        id: 'nav-stock-adj',
        label: 'Stock Adjustment',
        icon: <RefreshCw size={15} />,
        href: '/stock-adjustment',
      },
    ],
  },
  {
    id: 'project',
    label: 'Project',
    icon: <FolderKanban size={16} />,
    module: 'Project',
    defaultOpen: false,
    items: [
      {
        id: 'nav-project-dashboard',
        label: 'Project Dashboard',
        icon: <FolderKanban size={15} />,
        href: '/project',
      },
      {
        id: 'nav-tasks',
        label: 'Task Management',
        icon: <CheckSquare size={15} />,
        href: '/project/tasks',
      },
      {
        id: 'nav-timeline',
        label: 'Timeline',
        icon: <Calendar size={15} />,
        href: '/project/timeline',
      },
      {
        id: 'nav-engineer',
        label: 'Engineer Assignment',
        icon: <UserCog size={15} />,
        href: '/project/engineers',
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <BarChart2 size={16} />,
    defaultOpen: false,
    items: [
      {
        id: 'nav-sales-report',
        label: 'Sales Report',
        icon: <TrendingUp size={15} />,
        href: '/reports/sales',
        module: 'Sales',
      },
      {
        id: 'nav-subcon-margin',
        label: 'Margin Subkontraktor',
        icon: <Percent size={15} />,
        href: '/reports/subcon-margin',
        module: 'Sales',
      },
      {
        id: 'nav-finance-report',
        label: 'Finance Report',
        icon: <DollarSign size={15} />,
        href: '/reports/finance',
        module: 'Finance',
      },
      {
        id: 'nav-purchasing-report',
        label: 'Purchasing Report',
        icon: <ShoppingBag size={15} />,
        href: '/reports/purchasing',
        module: 'Purchasing',
      },
      {
        id: 'nav-inventory-report',
        label: 'Inventory Report',
        icon: <Package size={15} />,
        href: '/reports/inventory',
        module: 'Inventory',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <Settings size={16} />,
    module: 'Settings',
    defaultOpen: false,
    items: [
      {
        id: 'nav-company',
        label: 'Company Profile',
        icon: <Globe size={15} />,
        href: '/settings/company',
      },
      {
        id: 'nav-branch',
        label: 'Branch',
        icon: <GitBranch size={15} />,
        href: '/settings/branch',
      },
      {
        id: 'nav-users',
        label: 'User Management',
        icon: <Users size={15} />,
        href: '/settings/users',
      },
      {
        id: 'nav-roles',
        label: 'Role Management',
        icon: <Shield size={15} />,
        href: '/settings/roles',
      },
      {
        id: 'nav-demo-leads',
        label: 'Demo Leads',
        icon: <ClipboardCheck size={15} />,
        href: '/settings/demo-leads',
      },
      { id: 'nav-tax', label: 'Tax Settings', icon: <PieChart size={15} />, href: '/settings/tax' },
      {
        id: 'nav-numbering',
        label: 'Numbering Format',
        icon: <Hash size={15} />,
        href: '/settings/numbering',
      },
      {
        id: 'nav-preferences',
        label: 'ERP Preferences',
        icon: <Sliders size={15} />,
        href: '/settings/preferences',
      },
      {
        id: 'nav-system-admin',
        label: 'System Administration',
        icon: <Database size={15} />,
        href: '/settings/system-admin',
      },
      {
        id: 'nav-item-master-links',
        label: 'Item Belum Terhubung',
        icon: <Link2 size={15} />,
        href: '/settings/item-master-links',
      },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Some nav items share a URL prefix (e.g. Finance Reports at /finance-reports
// and Trial Balance at /finance-reports/trial-balance in a different group).
// Only the most specific (longest) matching href should count as active.
const findActiveHref = (pathname: string): string | undefined => {
  const allHrefs = navGroups.flatMap((g) => g.items.map((i) => i.href));
  const matches = allHrefs.filter((href) =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/')
  );
  return matches.sort((a, b) => b.length - a.length)[0];
};

export default function Sidebar({
  collapsed,
  onToggle,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const pathname = usePathname();
  const { data: companySettings } = useCompanySettings();
  const companyName = companySettings?.companyName || 'ERP System';

  // Below lg, the sidebar renders as a full-width slide-in drawer regardless of the
  // desktop "collapsed" preference — collapsing only makes sense once there's a
  // permanently-visible rail to collapse into.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const effectiveCollapsed = collapsed && !isMobile;

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!companySettings?.logoPath) {
      setLogoUrl(null);
      return;
    }
    let currentUrl: string | null = null;
    companySettingsService.getLogoObjectUrl().then((url) => {
      currentUrl = url;
      setLogoUrl(url);
    });
    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [companySettings?.logoPath]);

  // Same localStorage key + shape Topbar.tsx already reads correctly ({name, email, role} set at
  // login) — the footer below was never wired to it and showed a hardcoded mock name/role instead.
  const [loggedInUser, setLoggedInUser] = useState<{ name: string; role: string } | null>(null);
  useEffect(() => {
    const raw = localStorage.getItem('syntera_user');
    if (!raw) return;
    try {
      setLoggedInUser(JSON.parse(raw) as { name: string; role: string });
    } catch {}
  }, []);

  const showPendingApproval = hasAnyApprovePermission();
  const { data: pendingApprovals } = usePendingApprovals();
  const pendingCount = pendingApprovals?.length ?? 0;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    navGroups.forEach((g) => {
      init[g.id] = g.defaultOpen ?? false;
    });
    return init;
  });

  const activeHref = findActiveHref(pathname);
  const isActive = (href: string) => href === activeHref;

  const isGroupActive = (group: NavGroup) => group.items.some((item) => isActive(item.href));

  // Auto-open the group that contains the current page whenever route changes
  useEffect(() => {
    const activeHref = findActiveHref(pathname);
    navGroups.forEach((g) => {
      if (g.id === 'main') return;
      if (g.items.some((item) => item.href === activeHref)) {
        setOpenGroups((prev) => ({ ...prev, [g.id]: true }));
      }
    });
  }, [pathname]);

  const toggleGroup = (id: string) => {
    if (effectiveCollapsed) return;
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Dashboard group renders as a single direct link
  const dashboardGroup = navGroups.find((g) => g.id === 'main');

  // Each account only sees the modules its role has access to (an admin grants more via Role
  // Management). Groups with no `module` (Dashboard) are always visible. Reports has no
  // group-level module since it spans several — each item is filtered by its own `module` instead,
  // and the whole group is hidden if that leaves it empty.
  const otherGroups = navGroups
    .filter((g) => g.id !== 'main')
    .map((g) =>
      g.id === 'reports'
        ? { ...g, items: g.items.filter((item) => !item.module || hasModuleAccess(item.module)) }
        : g
    )
    .filter((g) =>
      g.id === 'reports' ? g.items.length > 0 : !g.module || hasModuleAccess(g.module)
    );

  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-50 flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out
        w-[var(--sidebar-width)] ${collapsed ? 'lg:w-[var(--sidebar-collapsed-width)]' : 'lg:w-[var(--sidebar-width)]'}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-border flex-shrink-0 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <AppLogo size={28} src={logoUrl ?? undefined} />
          {!effectiveCollapsed && (
            <span className="font-extrabold text-xl text-foreground tracking-tight whitespace-nowrap truncate">
              {companyName}
            </span>
          )}
        </div>
        <button
          onClick={onMobileClose}
          className="lg:hidden flex-shrink-0 min-w-11 min-h-11 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors -mr-2"
          aria-label="Tutup menu"
        >
          <X size={18} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 scrollbar-hide">
        {/* Dashboard direct link */}
        {dashboardGroup && (
          <div className="px-2 mb-1">
            <Link
              href="/"
              onClick={onMobileClose}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-500 transition-all duration-150 group relative
                ${
                  isActive('/')
                    ? 'bg-primary text-primary-foreground font-600'
                    : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                }`}
              title={effectiveCollapsed ? 'Dashboard' : undefined}
            >
              <span
                className={`flex-shrink-0 ${isActive('/') ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}
              >
                <LayoutDashboard size={16} />
              </span>
              {!effectiveCollapsed && <span className="truncate text-[13px]">Dashboard</span>}
            </Link>
          </div>
        )}

        {/* Pending Approval direct link — only for accounts with Approve permission somewhere */}
        {showPendingApproval && (
          <div className="px-2 mb-1">
            <Link
              href="/pending-approval"
              onClick={onMobileClose}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-500 transition-all duration-150 group relative
                ${
                  pathname === '/pending-approval'
                    ? 'bg-primary text-primary-foreground font-600'
                    : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                }`}
              title={effectiveCollapsed ? 'Pending Approval' : undefined}
            >
              <span
                className={`flex-shrink-0 ${pathname === '/pending-approval' ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}
              >
                <ClipboardCheck size={16} />
              </span>
              {!effectiveCollapsed && (
                <span className="flex-1 truncate text-[13px]">Pending Approval</span>
              )}
              {pendingCount > 0 && (
                <span
                  className={`flex-shrink-0 bg-red-500 text-white text-[10px] font-700 rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${effectiveCollapsed ? 'absolute top-0.5 right-0.5' : ''}`}
                >
                  {pendingCount}
                </span>
              )}
            </Link>
          </div>
        )}

        {/* Accordion groups */}
        {otherGroups.map((group) => {
          const isOpen = openGroups[group.id];
          const groupActive = isGroupActive(group);

          return (
            <div key={group.id} className="px-2 mb-0.5">
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-500 transition-all duration-150 group
                  ${
                    groupActive
                      ? 'text-primary bg-primary/5'
                      : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                  }`}
                title={effectiveCollapsed ? group.label : undefined}
              >
                <span
                  className={`flex-shrink-0 ${groupActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
                >
                  {group.icon}
                </span>
                {!effectiveCollapsed && (
                  <>
                    <span className="flex-1 text-left truncate font-600">{group.label}</span>
                    <span className="flex-shrink-0 text-muted-foreground">
                      {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </span>
                  </>
                )}
              </button>

              {/* Group items */}
              {!effectiveCollapsed && isOpen && (
                <ul className="mt-0.5 space-y-0.5 pl-3 border-l border-border ml-4">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <Link
                        href={item.href}
                        onClick={onMobileClose}
                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] font-500 transition-all duration-150 group relative
                          ${
                            isActive(item.href)
                              ? 'bg-primary text-primary-foreground font-600'
                              : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                          }`}
                      >
                        <span
                          className={`flex-shrink-0 ${isActive(item.href) ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}`}
                        >
                          {item.icon}
                        </span>
                        <span className="truncate">{item.label}</span>
                        {item.badge && (
                          <span
                            className={`ml-auto flex-shrink-0 text-[10px] font-700 rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${isActive(item.href) ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground'}`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {/* Collapsed: show active dot */}
              {effectiveCollapsed && groupActive && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-primary rounded-full" />
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 flex-shrink-0">
        {!effectiveCollapsed && (
          <div className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted cursor-pointer transition-colors mb-1">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User size={14} className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-600 text-foreground truncate">
                {loggedInUser?.name || 'Pengguna'}
              </p>
              <p className="text-xs text-muted-foreground truncate">{loggedInUser?.role || '—'}</p>
            </div>
            <Settings size={14} className="text-muted-foreground flex-shrink-0" />
          </div>
        )}
        {/* Collapse toggle — desktop only; on mobile the drawer is closed via the X button or overlay */}
        <button
          onClick={onToggle}
          className="hidden lg:flex w-full items-center justify-center gap-2 px-2.5 py-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 text-[13px] font-500"
        >
          {collapsed ? (
            <ChevronRight size={16} />
          ) : (
            <>
              <ChevronLeft size={16} />
              <span>Ciutkan</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
